/**
 * Avis sur la feuille de brouillon (admin) — migration 0696 : les étoiles laissées à la fin d'une feuille, les
 * idées et les problèmes signalés depuis l'icône du haut, avec l'exercice et les lignes écrites pour comprendre.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Lightbulb, RotateCcw, Star, TriangleAlert } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  reviewStats,
  useScratchFeedbackAdmin,
  useSetScratchFeedbackStatus,
  type FeedbackStatusFilter,
  type ScratchFeedbackKind,
  type ScratchFeedbackRow,
} from '@/lib/scratch-feedback';

const DATE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const KIND: Record<ScratchFeedbackKind, { label: string; cls: string }> = {
  review: { label: 'Avis', cls: 'bg-amber-50 text-amber-800' },
  idea: { label: 'Idée', cls: 'bg-primary-50 text-primary-700' },
  problem: { label: 'Problème', cls: 'bg-red-50 text-red-700' },
};
const CONTEXT: Record<ScratchFeedbackRow['context'], string> = {
  exercise: 'Exercice',
  tutorial: 'Tutoriel',
  demo: 'Démo',
  live: 'Live',
};

type KindFilter = 'all' | ScratchFeedbackKind;

/** Filtre pur (testé) : un genre, ou tout. */
export function filterFeedback(rows: ScratchFeedbackRow[], kind: KindFilter): ScratchFeedbackRow[] {
  return kind === 'all' ? rows : rows.filter((r) => r.kind === kind);
}

function Stars({ n }: { n: number }) {
  return (
    <span className="inline-flex" role="img" aria-label={`${n} étoile${n > 1 ? 's' : ''} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn('h-4 w-4', i <= n ? 'fill-amber-400 text-amber-400' : 'text-ink-200')}
          aria-hidden
        />
      ))}
    </span>
  );
}

function FeedbackCard({ row }: { row: ScratchFeedbackRow }) {
  const setStatus = useSetScratchFeedbackStatus();
  const k = KIND[row.kind];
  return (
    <div
      className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4"
      data-testid="feedback-card"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={cn('rounded-full px-2 py-0.5 font-semibold', k.cls)}>{k.label}</span>
        {row.stars != null && <Stars n={row.stars} />}
        <span className="bg-ink-50 text-ink-600 rounded-full px-2 py-0.5 font-semibold">
          {CONTEXT[row.context]}
        </span>
        <span className="text-ink-400">{DATE.format(new Date(row.created_at))}</span>
      </div>
      <p className="text-ink-700 text-sm">
        <strong className="text-ink-900">{row.author?.display_name ?? 'Un utilisateur'}</strong>
        {row.exercise_id && (
          <>
            {' · '}
            <Link
              to="/tutor/exercises/$exerciseId"
              params={{ exerciseId: row.exercise_id }}
              className="text-primary-600 underline"
            >
              {row.exercise?.title ?? 'l’exercice'}
            </Link>
          </>
        )}
      </p>
      {row.message && (
        <p className="text-ink-900 whitespace-pre-wrap break-words text-sm">{row.message}</p>
      )}
      {row.lines.length > 0 && (
        <details className="text-sm">
          <summary className="text-ink-500 min-h-[2.75rem] cursor-pointer py-2">
            Lignes écrites ({row.lines.length})
          </summary>
          <ol className="bg-ink-50 text-ink-800 list-decimal space-y-1 rounded-xl py-2 pl-8 pr-3 font-mono text-xs">
            {row.lines.map((l, i) => (
              <li key={i} className="break-words">
                {l}
              </li>
            ))}
          </ol>
        </details>
      )}
      <div className="flex flex-col sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() =>
            setStatus.mutate({ id: row.id, status: row.status === 'open' ? 'done' : 'open' })
          }
          disabled={setStatus.isPending}
          className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border px-4 text-sm font-semibold disabled:opacity-50"
        >
          {row.status === 'open' ? (
            <>
              <Check className="h-4 w-4" aria-hidden /> Marquer comme lu
            </>
          ) : (
            <>
              <RotateCcw className="h-4 w-4" aria-hidden /> Remettre à lire
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function TutorAdminScratchFeedbackPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [status, setStatus] = useState<FeedbackStatusFilter>('open');
  const [kind, setKind] = useState<KindFilter>('all');
  const { data: rows = [], isLoading, isError } = useScratchFeedbackAdmin(status, admin);

  if (!admin)
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );

  const stats = reviewStats(rows);
  const shown = filterFeedback(rows, kind);
  const kinds: { id: KindFilter; label: string; icon?: typeof Star }[] = [
    { id: 'all', label: 'Tout' },
    { id: 'review', label: 'Avis', icon: Star },
    { id: 'idea', label: 'Idées', icon: Lightbulb },
    { id: 'problem', label: 'Problèmes', icon: TriangleAlert },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="font-display text-ink-900 text-[24px] font-bold">Avis sur le brouillon</h1>
        <p className="text-ink-500 font-sans text-[14px]">
          Les étoiles laissées à la fin d’une feuille, les idées et les problèmes signalés depuis la
          feuille.
        </p>
        {stats.count > 0 && (
          <p className="text-ink-700 text-sm" data-testid="feedback-stats">
            Note moyenne : <strong>{stats.avg?.toLocaleString('fr-FR')} / 5</strong> ({stats.count}{' '}
            avis)
          </p>
        )}
      </header>

      <div role="tablist" aria-label="Statut" className="mb-3 flex gap-2">
        {(['open', 'done'] as const).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={status === f}
            onClick={() => setStatus(f)}
            className={cn(
              'min-h-[2.75rem] flex-1 rounded-xl border px-4 text-sm font-semibold sm:flex-none',
              status === f
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {f === 'open' ? 'À lire' : 'Lus'}
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Genre">
        {kinds.map((k) => (
          <button
            key={k.id}
            type="button"
            aria-pressed={kind === k.id}
            onClick={() => setKind(k.id)}
            className={cn(
              'inline-flex min-h-[2.75rem] items-center gap-1 rounded-full border px-3 text-sm font-semibold',
              kind === k.id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {k.icon && <k.icon className="h-4 w-4" aria-hidden />}
            {k.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Impossible de charger les avis.</p>
      ) : shown.length === 0 ? (
        <p className="text-ink-500 text-sm">Rien pour l’instant.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {shown.map((r) => (
            <FeedbackCard key={r.id} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}
